/**
 * This source code is quoted from rc-tabs.
 * homepage: https://github.com/react-component/tabs
 */
import classnames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useCallback, useReducer } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { debounce } from "../../wui-core/src/index";
import Dropdown from '../../wui-dropdown/src';
import Icon from '../../wui-icon/src';
import Menu from '../../wui-menu/src';
import { MenuProps } from '../../wui-menu/src/iMenu';
import { ScrollableTabBarNodeProps, TabBarState } from './iTabs';
import {
    requestAnimationFrame, setTransform, getPreciseLeft, tabBarReducer,
    getOffsetWH, getScrollWH, getOffsetLT, getTabItemDom, checkNeedTransform, isVertical,
    getDomNodes, buildNodeArray, parseTransformValue, getElementTotalSize, useSmartDebounce,
    getCurrentScrollPosition, DOMCache
} from './utils';

let seed = 0
const tabsUUID = () => `${new Date().getTime()}_${seed++}`

// Passive 事件支持检测 - 只执行一次
let supportsPassive = false;
try {
    const opts = Object.defineProperty({}, 'passive', {
        get: () => {
            supportsPassive = true;
            return true;
        }
    });
    window.addEventListener('test', null as any, opts);
    window.removeEventListener('test', null as any);
} catch (e) {
    // passive not supported
}

const ScrollableTabBarNode: React.FC<ScrollableTabBarNodeProps> = (props) => {
    const {
        clsPrefix = '', scrollAnimated = true, navWrapper = (ele: any) => ele, prevIcon, nextIcon, moreType,
        fieldid, dropdownClassName, moreIcon, moreTypeAllShow, type,
        tabBarPosition = 'left', dir, isTruncationShow, dragable, getRef, saveRef,
        panels, activeKey, isEditNum, items, onEdit, onTabClick,
        onPrevClick, onNextClick, children
    } = props;

    // 使用 useReducer 替代多个 useState，但保持向后兼容
    const [state, dispatch] = useReducer(tabBarReducer, {
        next: false,
        prev: false,
        menuArr: '',
        menuArrowLeft: '',
        menuArrowRight: '',
        tabsItems: [],
        allItemW: 0,
        deltaY: 0
    });

    // 解构 state 以保持代码兼容性
    const { next, prev, menuArr, menuArrowLeft, menuArrowRight, tabsItems, allItemW, deltaY } = state;

    const offsetRef = useRef<number>(0);
    const isPrevRef = useRef<boolean>(false);
    const debouncedResizeRef = useRef<any>();
    const resizeObserverRef = useRef<any>();
    const itemMenuIndexRef = useRef<number>(0);
    const tabsUidRef = useRef<string>(tabsUUID());
    const prevRef = useRef<boolean>(false);
    const nextRef = useRef<boolean>(false);
    const deltaYRef = useRef<number>(0);
    const prevMoreTypeRef = useRef<string | undefined>();
    const isResizeObserverInitialCall = useRef<boolean>(true);
    const prevPanelsLengthRef = useRef<number | undefined>();
    const allTabsFitInFirstScreenRef = useRef<boolean>(false);

    // 添加 DOM 缓存实例
    const domCacheRef = useRef<DOMCache>(new DOMCache({ cacheTime: 100, maxSize: 50 }));

    // 统一管理 requestAnimationFrame 和 setTimeout
    const rafIdsRef = useRef<Set<number>>(new Set());
    // eslint-disable-next-line no-undef
    const timeoutIdsRef = useRef<Set<NodeJS.Timeout>>(new Set());
    const observedElementRef = useRef<HTMLElement | null>(null);

    // 创建菜单项 - 使用 useCallback 优化闭包
    const createMenuItems = useCallback((items: any[], isLeft?: string) => {
        return items.map((item) => {
            if (!item) return null;
            const parsed = JSON.parse(item);
            return <Menu.Item fieldid={parsed?.fieldid} key={parsed.key} langid={parsed.langid}>
                {parsed.title}
                {iseditCardTabs(item, isLeft)}
            </Menu.Item>
        });
    }, [type, onEdit]);

    // 更新左右菜单 - 使用批量更新优化
    const updateMenus = (startIndex: number, endIndex: number, atherArr: any[], scrollW?: number) => {
        const menuLeft: any[] = [];
        const menuRight: any[] = [];

        atherArr.forEach(item => {
            const parsed = JSON.parse(item);
            if (parsed.index <= startIndex) menuLeft.push(item);
            if (parsed.index > endIndex) menuRight.push(item);
        });

        const menuArrowLeftItems = createMenuItems(menuLeft, 'isLeft');
        const menuArrowRightItems = createMenuItems(menuRight);

        // 批量更新菜单状态
        const updates: Partial<TabBarState> = {
            menuArrowLeft: menuArrowLeftItems,
            menuArrowRight: menuArrowRightItems
        };

        if (scrollW !== undefined) {
            deltaYRef.current = scrollW;
            updates.deltaY = scrollW;
        }

        dispatch({ type: 'BATCH_UPDATE', payload: updates });
    };

    // 更新tabs状态 - 延迟定义以避免循环引用 注意：这个函数在初始化时会调用还未定义的函数，所以需要延迟执行
    const updateTabsState = (menuItemIndex?: number) => {
        const rafId = requestAnimationFrame(() => {
            rafIdsRef.current.delete(rafId);
            if (typeof getMenuItem === 'function') getMenuItem(menuItemIndex !== undefined ? menuItemIndex : 0);
            if (typeof setNextPrev === 'function') setNextPrev();
            if (typeof updateScrollMenus === 'function') updateScrollMenus('');
        });
        rafIdsRef.current.add(rafId);
    };

    // 初始化
    useLayoutEffect(() => {
        const rafId = requestAnimationFrame(() => {
            rafIdsRef.current.delete(rafId);
            getMenuItem(0);
            scrollToActiveTab();
            updateScrollMenus('');
        });
        rafIdsRef.current.add(rafId);

        debouncedResizeRef.current = debounce(() => {
            // 跳过 ResizeObserver 的初始调用，避免重复执行 getMenuItem
            if (isResizeObserverInitialCall.current) {
                isResizeObserverInitialCall.current = false;
                return;
            }

            setNextPrev();
            if (moreType === 'moreTabsSelect') {
                // 先获取当前的偏移量，保存resize前的状态
                const currentOffset = offsetRef.current;

                getMenuItem(0);

                // 如果resize后所有tabs都能在第一屏显示，需要重置偏移量
                if (allTabsFitInFirstScreenRef.current && currentOffset !== 0) {
                    setOffset(0, false);
                }

                scrollToActiveTab();
                upDataItem();
            }
        }, 200);

        // 保存被观察的元素引用
        const containerElement = getRef?.('container');
        if (containerElement) {
            observedElementRef.current = containerElement;
            resizeObserverRef.current = new ResizeObserver(debouncedResizeRef.current);
            resizeObserverRef.current.observe(containerElement);
        }

        // 保存事件处理函数引用
        const keydownHandler = handleArrow;
        document.addEventListener('keydown', keydownHandler);

        // Cleanup - 完善清理逻辑
        return () => {
            // 清理所有 RAF
            rafIdsRef.current.forEach(id => cancelAnimationFrame(id));
            rafIdsRef.current.clear();

            // 清理所有 setTimeout
            timeoutIdsRef.current.forEach(id => clearTimeout(id));
            timeoutIdsRef.current.clear();

            // 清理 ResizeObserver
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }

            // 清理防抖函数
            if (debouncedResizeRef.current?.cancel) {
                debouncedResizeRef.current.cancel();
                debouncedResizeRef.current = null;
            }

            // 清理 DOM 缓存
            if (domCacheRef.current) {
                domCacheRef.current.clear();
            }

            // 移除事件监听器
            document.removeEventListener('keydown', keydownHandler);

            // 清理保存的元素引用
            observedElementRef.current = null;
        };
    }, []);


    // panels 更新处理 - 只在真正的增删操作时执行
    useEffect(() => {
        // 跳过初始化和非真实变化
        if (!prevPanelsLengthRef.current) {
            prevPanelsLengthRef.current = panels?.length;
            return; // 初始化时不执行
        }

        // 只有当 panels 长度真正变化时才执行（增删标签页）
        if (panels && panels.length > 0 && panels.length !== prevPanelsLengthRef.current) {
            prevPanelsLengthRef.current = panels.length;

            const rafId = requestAnimationFrame(() => {
                rafIdsRef.current.delete(rafId);
                const domNode = getDomNodes(dragable, getRef);
                const isEditNumChanged = isEditNum !== undefined;
                let menuItemIndex = 0;

                if (isEditNumChanged) {
                    if (isEditNum === domNode.length) {
                        menuItemIndex = isEditNum - 1;
                        getMenuItem(menuItemIndex); // 先同步更新 allTabsFitInFirstScreenRef
                        setNextPrev();
                        updateScrollMenus('');
                        upDataItem(); // 再调用 upDataItem，此时已有最新的判断值
                    } else {
                        updateTabsState(isEditNum);
                    }
                } else {
                    if (isEditNum === domNode?.length) {
                        menuItemIndex = isEditNum - 1;
                    } else if (isEditNum! + 1 === domNode?.length) {
                        menuItemIndex = isEditNum || 0;
                    }
                    updateTabsState(menuItemIndex);
                }
            });
            rafIdsRef.current.add(rafId);
        }
    }, [panels?.length]);

    useEffect(() => {
        if (items && items.length > 0) {
            let num = 0;
            items.forEach((item: any, index: number) => {
                if (activeKey === item.key) num = index;
            });
            const rafId = requestAnimationFrame(() => {
                rafIdsRef.current.delete(rafId);
                getMenuItem(num);
            });
            rafIdsRef.current.add(rafId);
        }
    }, [items]);

    useEffect(() => {
        if ('moreType' in props && prevMoreTypeRef.current && prevMoreTypeRef.current !== moreType) {
            if (moreType === 'moreTabsSelect' && getRef?.('container') && tabBarPosition === 'top') {
                getRef('container').style.paddingRight = '32px';
                getRef('container').style.paddingLeft = '0';
                getMenuItem(0);
            }
        }
        prevMoreTypeRef.current = moreType;
    }, [moreType]);

    // 生命周期钩子 - componentDidUpdate for activeKey and tabBarPosition
    useEffect(() => {
        const rafId = requestAnimationFrame(() => {
            rafIdsRef.current.delete(rafId);
            // activeKey变化时立即执行，不防抖
            if (moreTypeAllShow || moreType === 'moreTabsArrow') setNextPrev(true);
            scrollToActiveTab();
        });
        rafIdsRef.current.add(rafId);
    }, [activeKey, tabBarPosition]);

    useEffect(() => {
        prevRef.current = prev;
        nextRef.current = next;
        deltaYRef.current = deltaY;
        setNextPrev();
    }, [prev, next, deltaY]);


    // 左右箭头监听事件
    const handleArrow = useCallback((event: any) => {
        if (event.keyCode === 37 || event.keyCode === 39) {
            const rafId = requestAnimationFrame(() => {
                rafIdsRef.current.delete(rafId);
                updateScrollMenus('');
            });
            rafIdsRef.current.add(rafId);
        }
    }, []);

    // 使用智能防抖优化setNextPrev，避免频繁触发
    const setNextPrevOriginal = useCallback(() => {
        if (allTabsFitInFirstScreenRef.current && !moreTypeAllShow) {
            prevRef.current = false;
            nextRef.current = false;
            dispatch({ type: 'BATCH_UPDATE', payload: { prev: false, next: false } });
            return { next: false, prev: false };
        }

        const navNode = getRef?.('nav');
        const navTabsContainer = getRef?.('navTabsContainer');
        const navNodeWH: number = getScrollWH((navTabsContainer as HTMLElement) || navNode, tabBarPosition);
        let currentPrev = prevRef.current;
        let currentNext = nextRef.current;
        let offset = offsetRef.current;
        if (moreType === 'moreTabsArrow') {
            const container = getRef?.('container');
            if (container) {
                container.style.paddingLeft = currentPrev ? '32px' : '0';
                if (!currentNext) {
                    container.style.paddingRight = moreTypeAllShow && offset < -1 ? '32px' : '0';
                } else {
                    container.style.paddingRight = moreTypeAllShow ? '64px' : '32px';
                }
            }
        }
        const containerWH = (getOffsetWH!(getRef!('container'), tabBarPosition) as number) + 1;
        const navWrapNodeWH: number = getOffsetWH!(getRef!('navWrap'), tabBarPosition);
        const minOffset = containerWH - navNodeWH;
        if (minOffset >= 0) {
            if (navNodeWH != 0) { // 所属容器为0的情况为当整个tabs display为none时存在，此时不应更新移动位置
                currentNext = false;
                setOffset(0, false);
                offset = 0;
            }
        } else if (minOffset < offset) {
            currentNext = true;
        } else {
            currentNext = false;
            if (navNodeWH != 0) {
                const realOffset = navWrapNodeWH - navNodeWH;
                setOffset(realOffset, false);
                offset = realOffset;
            }
        }

        currentPrev = offset < 0 ? offset !== -32 : false;
        if (isTruncationShow && moreTypeAllShow) { // 特殊场景，左树每点击一个节点添加一项tabs，并更新组件
            let isPrev = isPrevRef.current;
            let activeTab: HTMLElement | undefined | null = getRef?.('activeTab');
            let activeKey = activeTab?.getAttribute('nodekey')
            let domNode = getDomNodes(dragable, getRef);
            let atherArr = buildNodeArray(domNode);
            const navNode = getRef?.('nav');
            const translateValues = parseTransformValue(navNode?.style?.transform);
            if (JSON.parse(atherArr[atherArr?.length - 1])?.key === activeKey && !isPrev) {
                currentNext = false
            } else if (isPrev) {
                currentNext = true
            }
            const translateValue = parseFloat(translateValues![0]);
            if (translateValue && translateValue != 0) currentPrev = true
        }

        prevRef.current = currentPrev;
        nextRef.current = currentNext;
        dispatch({ type: 'BATCH_UPDATE', payload: { prev: currentPrev, next: currentNext } });

        return { next: currentNext, prev: currentPrev };
    }, [getRef, tabBarPosition, moreType, moreTypeAllShow]);

    // 使用智能防抖包装setNextPrev，但提供立即执行的选项
    const setNextPrevDebounced = useSmartDebounce(setNextPrevOriginal, [setNextPrevOriginal]);

    // 创建一个条件执行的setNextPrev，根据场景决定是否防抖
    const setNextPrev = useCallback((immediate = false) => {
        immediate ? setNextPrevOriginal() : setNextPrevDebounced();
    }, [setNextPrevOriginal, setNextPrevDebounced]);

    const setOffset = (offset: number, checkNextPrev = true) => {
        const navWrapNode = getRef?.('navWrap');
        const navWrapNodeWH = getOffsetWH(navWrapNode as HTMLElement, tabBarPosition);

        // 计算目标偏移量
        let target = Math.min(0, offset);
        if (dir === 'rtl') {
            const navTabsContainer = getRef?.('navTabsContainer');
            const navTabsContainerWH = getOffsetWH(navTabsContainer as HTMLElement, tabBarPosition);
            target = Math.min(offset, navTabsContainerWH - navWrapNodeWH);
            target = Math.max(target, 0);

            // editable-card 特殊处理
            if (type === 'editable-card') {
                const domNode = getDomNodes(dragable, getRef);
                const lastTab = domNode[domNode.length - 1] as HTMLElement;
                const navNode = getRef?.('nav');
                const totalWidth = getPreciseLeft(lastTab, navNode as HTMLElement) + lastTab.offsetWidth;
                target = Math.max(Math.min(offset, totalWidth - navWrapNodeWH), 0);
            }
        }

        // 特殊情况：保持当前偏移
        if (isTruncationShow && moreTypeAllShow && isPrevRef.current) {
            const navNode = getRef?.('nav');
            const translateValues = parseTransformValue(navNode?.style?.transform);
            const currentOffset = parseFloat(translateValues[0]);
            if (!isNaN(currentOffset)) offsetRef.current = currentOffset;
        }

        if (offsetRef.current === target) return;
        offsetRef.current = target;
        const navStyle = getRef?.('nav').style as React.CSSProperties;
        const transformSupported = 'transform' in navStyle || 'webkitTransform' in navStyle || 'MozTransform' in navStyle;

        // 应用偏移量
        if (transformSupported) {
            setTransform(navStyle, isVertical(tabBarPosition) ? `translate3d(0,${target}px,0)` : `translate3d(${target == -32 ? 0 : target}px,0,0)`);
        } else {
            (navStyle as any)[isVertical(tabBarPosition) ? 'top' : 'left'] = `${target}px`;
        }

        if (checkNextPrev) setNextPrev();
    };

    const getMenuItem = useCallback((itemIndex = 0) => {
        itemMenuIndexRef.current = itemIndex;

        // 尝试从缓存获取
        const cacheKey = `menu_${itemIndex}_${activeKey}_${tabBarPosition}`;
        const cached = domCacheRef.current.get(cacheKey);
        if (cached) {
            dispatch({ type: 'BATCH_UPDATE', payload: { menuArr: cached.menuArrItems } });
            return;
        }

        const navWrapNode = getRef?.('navWrap');
        const navWrapNodeWH = getOffsetWH(navWrapNode as HTMLElement, tabBarPosition);
        const domNode = getDomNodes(dragable, getRef);
        const direction = isVertical(tabBarPosition) ? 'vertical' : 'horizontal';

        // 批量读取所有 DOM 尺寸，避免循环中多次回流
        const tabSizes = domNode.map((_, i) => {
            const tabItem = getTabItemDom(i, dragable, getRef);
            return tabItem ? getElementTotalSize(tabItem, direction) : 0;
        });

        // 计算第一屏能显示的项，同时构建 tabsItems 数据
        let initFirstPageWidth = 0;
        let allTabsFitInFirstScreen = true;
        const tabsItemsData: any[] = [];
        let totalWidth = 0;

        for (let i = 0; i < tabSizes.length; i++) {
            if (tabSizes[i] > 0) {
                initFirstPageWidth += tabSizes[i];
                totalWidth += tabSizes[i];

                // 构建 tabsItems 数据（用于滚动计算）
                tabsItemsData.push({
                    index: i,
                    width: tabSizes[i]
                });

                if (initFirstPageWidth > navWrapNodeWH && navWrapNodeWH > 0) allTabsFitInFirstScreen = false;
            }
        }

        // 如果所有标签都能在第一屏显示，直接返回空菜单
        if (allTabsFitInFirstScreen) {
            allTabsFitInFirstScreenRef.current = true; // 更新 ref
            const atherArr = buildNodeArray(domNode);
            const menuArrItems = atherArr.length > 0 ? createMenuItems(atherArr, 'isLeft') : [];
            domCacheRef.current.set(cacheKey, { menuArrItems });

            // 更新 tabsItems（虽然不需要滚动，但保持数据一致性）
            dispatch({
                type: 'BATCH_UPDATE',
                payload: {
                    menuArr: menuArrItems,
                    tabsItems: [], // 不需要滚动时为空
                    allItemW: totalWidth
                }
            });
            return;
        }

        // 需要滚动，更新 ref，更新 tabsItems 供后续滚动计算使用
        allTabsFitInFirstScreenRef.current = false;
        dispatch({
            type: 'BATCH_UPDATE',
            payload: {
                tabsItems: tabsItemsData,
                allItemW: totalWidth
            }
        });

        // 构建菜单数组
        const atherArr = buildNodeArray(domNode);
        const menuArrItems = createMenuItems(atherArr, 'isLeft');

        // 存入缓存
        domCacheRef.current.set(cacheKey, { menuArrItems });

        dispatch({ type: 'BATCH_UPDATE', payload: { menuArr: menuArrItems } });
    }, [activeKey, tabBarPosition, dragable, getRef, createMenuItems]);

    // editable-card类型tabs，下拉项根据closable添加关闭按钮
    const iseditCardTabs = (item: string, isLeft?: string) => {
        if (type !== 'editable-card') return;
        const parsed = JSON.parse(item);
        if (parsed.closable !== 'noClose') return (<Icon onClick={(e: any) => menuItemClickHandle(parsed.key, isLeft, e)} style={{position: 'absolute', right: '0', fontSize: '12px'}} type="uf-close" />)
    };
    // 点击下拉项内的关闭图标触发
    const menuItemClickHandle = (key: string, isLeft: string | undefined, e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (onEdit) onEdit(key, 'remove');
        const navNode = getRef?.('nav');
        const translateValues = parseTransformValue(navNode?.style?.transform);
        if (isLeft && isLeft === 'isLeft') { // 点击左侧截断关闭，之前的偏移量应更新
            if (!isNaN(parseFloat(translateValues[0]))) {
                updateScrollMenus('')
                setOffset(parseFloat(translateValues[0] + 150))
            }
        }
    };

    // 合并后的选择处理方法
    const onSelect: MenuProps['onSelect'] = (key: any) => {
        isPrevRef.current = false;

        const navWrapNode = getRef?.('navWrap');
        const navWrapNodeWH = getOffsetWH(navWrapNode as HTMLElement, tabBarPosition);
        const needTransform = checkNeedTransform(navWrapNode as HTMLElement);
        const domNode = getDomNodes(dragable, getRef);
        let itemIndex = 0;

        // 找到点击的标签索引并设置偏移量
        domNode.forEach((item, index) => {
            if (item.getAttribute('nodekey') == key.key) {
                itemIndex = index;
                const targetTab = getTabItemDom(index, dragable, getRef);

                if (needTransform) {
                    // 缩放情况下的处理
                    const timeoutId = setTimeout(() => {
                        timeoutIdsRef.current.delete(timeoutId);
                        let totalWidth = 0;
                        let currentNode = targetTab;
                        while (currentNode.previousElementSibling) {
                            totalWidth += getElementTotalSize(currentNode.previousElementSibling as HTMLElement, 'horizontal');
                            currentNode = currentNode.previousElementSibling as HTMLElement;
                        }
                        totalWidth += targetTab.offsetWidth;
                        const offset = totalWidth - navWrapNodeWH;
                        setOffset(-Math.max(0, offset));
                    }, 0);
                    timeoutIdsRef.current.add(timeoutId);
                } else {
                    // 正常情况下的处理
                    if (tabBarPosition === 'left' || tabBarPosition === 'right') {
                        const navRect = (getRef?.('nav') as HTMLElement).getBoundingClientRect();
                        const tabRect = targetTab.getBoundingClientRect();
                        setOffset(navRect.top - tabRect.top + navWrapNodeWH - tabRect.height);
                    } else if (dir === 'rtl') {
                        let offset = offsetRef.current;
                        const wrapOffset = getOffsetLT(navWrapNode as HTMLElement, tabBarPosition);
                        const activeTabOffset = getOffsetLT(targetTab, tabBarPosition);
                        offset += (wrapOffset - activeTabOffset);
                        setOffset(offset);
                    } else {
                        const navRect = (getRef?.('nav') as HTMLElement).getBoundingClientRect();
                        const tabRect = targetTab.getBoundingClientRect();
                        setOffset(navRect.left - tabRect.left + navWrapNodeWH - tabRect.width);
                    }
                }
            }
        });

        getMenuItem(itemIndex);
        onTabClick?.(key.key);

        // 更新翻页截断内容项 - 点击菜单项时立即更新
        if (!isTruncationShow) return;
        updateScrollMenus(needTransform ? '' : 'select', true);
    };


    // 全量下拉显示时定位到选中项
    const onVisibleChange = () => {
        const activeTab = getRef?.('activeTab');
        const activeKey = activeTab?.getAttribute('nodekey');
        const domNode = getDomNodes(dragable, getRef);
        const atherArr = buildNodeArray(domNode);
        const scrollIndex = atherArr.findIndex(item => JSON.parse(item)?.key === activeKey);

        if (scrollIndex >= 0) {
            const timeoutId = setTimeout(() => {
                timeoutIdsRef.current.delete(timeoutId);
                const dropdown = document.querySelector(`.${clsPrefix}-more-select-dropdown-${tabsUidRef.current}`);
                if (dropdown) dropdown.scrollTop = (scrollIndex - 6) * 32;
            }, 200);
            timeoutIdsRef.current.add(timeoutId);
        }
    };

    const scrollToActiveTab = (e?: {target: any, currentTarget: any}): undefined | null => {
        const activeTab = getRef?.('activeTab');
        const navWrap = getRef?.('navWrap');

        // 提前返回条件
        if (e && e.target !== e.currentTarget || !activeTab || !navWrap) return;

	    const needToScroll = nextRef.current || prevRef.current;
	    if (!needToScroll && !moreTypeAllShow) return;

	    // 获取尺寸和位置信息
	    const activeTabWH = getScrollWH(activeTab, tabBarPosition);
	    const navWrapNodeWH = getOffsetWH(navWrap as HTMLElement, tabBarPosition);
	    const wrapOffset = getOffsetLT(navWrap as HTMLElement, tabBarPosition);
	    const activeTabOffset = getOffsetLT(activeTab, tabBarPosition);

	    // 计算偏移调整
	    let offset = offsetRef.current;
	    let adjustAmount = 0;

	    if (wrapOffset > activeTabOffset) {
	        // 标签在左侧，需要向右滚动
	        adjustAmount = wrapOffset - activeTabOffset;
	        offset += adjustAmount;
	        setOffset(offset);
	    } else if ((wrapOffset + navWrapNodeWH) < (activeTabOffset + activeTabWH)) {
	        // 标签在右侧，需要向左滚动
	        const baseAdjust = (activeTabOffset + activeTabWH) - (wrapOffset + navWrapNodeWH);
	        const arrowPadding = moreType === 'moreTabsArrow' ? 32 : 0;
	        const extraPadding = isTruncationShow && moreTypeAllShow ? arrowPadding : 0;
	        const cardPadding = type === 'editable-card' ? 3 : 0;

	        adjustAmount = -(baseAdjust + extraPadding + cardPadding);
	        offset += adjustAmount;
	        setOffset(offset);
	    }

	    // 更新菜单项（如果需要）
	    const activeTabWidth = activeTab.getBoundingClientRect().width;
	    if (adjustAmount > 0 && adjustAmount < activeTabWidth) {
	        getTabsItemKey(itemMenuIndexRef.current - 1);
	    } else if (Math.abs(adjustAmount) < activeTabWidth && adjustAmount <= 0) {
	        getTabsItemKey();
	    }
    };

    // 点击tabs节点(极值当多页签左右显示不全时，点击极值之后下拉项更新)
    const getTabsItemKey = (num = 0) => {
        num !== 0 ? getMenuItem(num) : getItem();
    };

    const getItem = () => {
        const domNode = getDomNodes(dragable, getRef);
        const activeTab = getRef?.('activeTab');
        const activeKey = activeTab?.getAttribute('nodekey');
        const activeIndex = domNode.findIndex(item => item.getAttribute('nodekey') === activeKey);

        if (activeIndex >= 0) getMenuItem(activeIndex);
    };

    // 嵌套多页签，内部页签存在位移时，更新下拉项
    const upDataItem = () => {
        // 如果所有标签都能在第一屏显示，不需要更新
        if (allTabsFitInFirstScreenRef.current && !moreTypeAllShow) {
            return;
        }

        const domNode = getDomNodes(dragable, getRef);
        const activeTab = getRef?.('activeTab');
        const activeKey = activeTab?.getAttribute('nodekey');
        const navWrapNode = getRef?.('navWrap');

        if (!activeTab || !navWrapNode || !activeKey) {
            getItem();
            return;
        }

        const activeRect = activeTab.getBoundingClientRect();
        const wrapRect = navWrapNode.getBoundingClientRect();
        const activeRelativePos = activeRect.left - wrapRect.left;

        // 如果激活标签在可视区域内
        if (activeRelativePos + activeRect.width < wrapRect.width) {
            const activeIndex = domNode.findIndex(item => item.getAttribute('nodekey') === activeKey);

            // 计算第一屏能显示的项
            let totalWidth = 0;
            let isInFirstScreen = false;
            for (let i = 0; i <= activeIndex; i++) {
                totalWidth += getElementTotalSize(domNode[i] as HTMLElement, 'horizontal');
                if (totalWidth <= wrapRect.width && i === activeIndex) isInFirstScreen = true;
            }

            if (isInFirstScreen) {
                // 激活项在第一屏，重置到开始
                getMenuItem(0);
                setOffset(0);
            } else {
                // 计算需要显示的最后一个项
                const remainingWidth = wrapRect.width - activeRelativePos;
                let visibleWidth = 0;
                let lastVisibleIndex = activeIndex;

                for (let i = activeIndex; i < domNode.length; i++) {
                    visibleWidth += domNode[i]?.getBoundingClientRect().width;
                    if (visibleWidth < remainingWidth) {
                        lastVisibleIndex = i;
                    } else {
                        break;
                    }
                }

                getMenuItem(lastVisibleIndex);
            }

            // scrollToActiveTab被调用时，立即更新菜单
            if (isTruncationShow) updateScrollMenus('', true);
        } else {
            getItem();
        }
    };

    // 辅助方法：处理导航点击
    const handleNavClick = (direction: 'prev' | 'next', e: React.MouseEvent) => {
        isPrevRef.current = direction === 'prev';

        const handler = direction === 'prev' ? onPrevClick : onNextClick;
        handler!(e);

        const navWrapNode = getRef!('navWrap');
        const navWrapNodeWH = getOffsetWH(navWrapNode as HTMLElement, tabBarPosition);
        const newOffset = direction === 'prev' ? offsetRef.current + navWrapNodeWH : offsetRef.current - navWrapNodeWH;
        setOffset(newOffset);

        updateScrollMenus('');
    };

    // 计算索引范围
    const calculateIndexRange = (items: any[], scrollW: number, navWrapNodeWH: number, allItemW: number, isRealTime = false, val = '') => {
        let startIndex = 0;
        let endIndex = 0;
        let startWidth = 0;

        const processItem = (itemWidth: number, index: number) => {
            startWidth += itemWidth;
            // 计算左侧截断索引
            if ((allItemW - (scrollW + navWrapNodeWH) < 1 && startWidth <= scrollW - 32 && startWidth <= allItemW) ||
                startWidth <= scrollW && startWidth <= allItemW) {
                startIndex = index + 1;
            }
            // 计算右侧截断索引
            if (offsetRef.current === 0) {
                if (startWidth <= scrollW + navWrapNodeWH + 32) endIndex = index;
            } else {
                if ((val === 'select' && startWidth <= scrollW + navWrapNodeWH + 32) || startWidth <= scrollW + navWrapNodeWH - 32) endIndex = index;
            }
        };

        if (isRealTime) {
            items.forEach((item, index) => {
                const itemWidth = getElementTotalSize(item as HTMLElement, 'horizontal');
                processItem(itemWidth, index);
            });
        } else {
            items.forEach((item) => {
                processItem(item.width, item.index);
            });
        }

        return { startIndex, endIndex };
    };

    // 合并后的方法：处理鼠标滚动和缩放情况
    const updateScrollMenusOriginal = useCallback((val: string) => {
        if (allTabsFitInFirstScreenRef.current && !isTruncationShow && !moreTypeAllShow) {
            return;
        }

        const navWrapNode = getRef!('navWrap');
        const navWrapNodeWH = getOffsetWH(navWrapNode as HTMLElement, tabBarPosition);
        const navNode = getRef?.('nav');
        const needTransform = checkNeedTransform(navWrapNode as HTMLElement);

        // 处理缩放情况下的激活标签定位
        if (needTransform) {
            const activeTab = getRef?.('activeTab');
            if (activeTab && navNode?.children[0]) {
                const navChildren = navNode.children[0];
                const domNode = dragable && navChildren.children[0]?.children ? Array.prototype.slice.call(navChildren.children[0].children) : Array.prototype.slice.call(navChildren.children);
                const itemIndex = domNode.findIndex(item => item === activeTab);

                if (itemIndex !== -1) {
                    const targetTab = getTabItemDom(itemIndex, dragable, getRef);
                    let totalWidth = 0;
                    let currentNode = targetTab;

                    // 计算从开始到目标标签的总宽度
                    while (currentNode.previousElementSibling) {
                        totalWidth += getElementTotalSize(currentNode.previousElementSibling as HTMLElement, 'horizontal');
                        currentNode = currentNode.previousElementSibling as HTMLElement;
                    }
                    totalWidth += targetTab.offsetWidth;

                    // 调整偏移量
                    const offsetEnd = totalWidth - navWrapNodeWH;
                    const offsetStart = totalWidth - targetTab.offsetWidth;

                    if (totalWidth > navWrapNodeWH - offsetRef.current) {
                        setOffset(-Math.max(0, offsetEnd));
                    } else if (totalWidth - targetTab.offsetWidth < -offsetRef.current) {
                        setOffset(-Math.max(0, offsetStart));
                    }
                }
            }
        }

        if (!isTruncationShow) return;

        // 获取滚动位置
        const scrollW = needTransform ? parseFloat(getComputedStyle(navNode as HTMLElement).left) || 0 : getCurrentScrollPosition(navNode);

        // 获取DOM节点和构建数组
        const domNode = getDomNodes(dragable, getRef);
        const atherArr = buildNodeArray(domNode);
        const tabsItemsData = tabsItems;
        const allItemWData = allItemW || 0;

        // 计算索引范围并更新菜单
        const { startIndex, endIndex } = (!needTransform && tabsItemsData?.length > 0)
            ? calculateIndexRange(tabsItemsData, scrollW, navWrapNodeWH, allItemWData, false, val)
            : calculateIndexRange(domNode, scrollW, navWrapNodeWH, allItemWData, true, val);

        updateMenus(startIndex, endIndex, atherArr, scrollW);
    }, [getRef, tabBarPosition, isTruncationShow, dragable, activeKey]);

    // 使用智能防抖包装updateScrollMenus，但提供立即执行的选项
    const updateScrollMenusDebounced = useSmartDebounce(updateScrollMenusOriginal, [updateScrollMenusOriginal]);

    // 创建一个条件执行的updateScrollMenus，根据场景决定是否防抖
    const updateScrollMenus = useCallback((val: string, immediate = false) => {
        if (immediate) {
            updateScrollMenusOriginal(val);
        } else {
            updateScrollMenusDebounced(val);
        }
    }, [updateScrollMenusOriginal, updateScrollMenusDebounced]);

    const mouseScroll = useCallback((e: any) => { // 鼠标滚动切换页签
        e.stopPropagation()
        e.preventDefault()
        const deltalNum = e?.deltaY || e?.detail || 0
        if ((deltalNum < 0 && !prevRef.current || deltalNum > 0 && !nextRef.current) && dir !== 'rtl') { // 开始或结束之后不在做操作
            return
        }
        const currentDeltaY = deltaYRef.current + e?.deltaY;
        deltaYRef.current = currentDeltaY; // 立即更新 ref
        dispatch({ type: 'BATCH_UPDATE', payload: { deltaY: currentDeltaY } });

        const navWrapNode = getRef!('navWrap');
        const navWrapNodeWH: number = getOffsetWH(navWrapNode as HTMLElement, tabBarPosition);
        const domNode = getDomNodes(dragable, getRef);
        const atherArr = buildNodeArray(domNode);
        const scrollW = currentDeltaY || 0;
        let startIndex = 0, endIndex = 0, startWidth = 0;
        // 如果 滚动的距离 > 总页签宽度 - 容器宽度 + 左右padding  说明向右滚动到头部了，不需要在进行后续逻辑
        if (scrollW > allItemW - navWrapNodeWH + 32) {
            const newDeltaY = allItemW - navWrapNodeWH - e?.deltaY;
            deltaYRef.current = newDeltaY;
            dispatch({ type: 'BATCH_UPDATE', payload: { deltaY: newDeltaY } });
            return
        }
        if (tabsItems?.length === 0) return
        tabsItems.forEach((item) => {
            startWidth += item.width
            if (startWidth <= scrollW && startWidth <= allItemW) {
                startIndex = item.index + 1
            }
            if ((offsetRef.current === -100 && startWidth <= scrollW + navWrapNodeWH + 32) || startWidth <= scrollW + navWrapNodeWH) {
                endIndex = item.index
            }
        })
        updateMenus(startIndex, endIndex, atherArr);
        setOffset(offsetRef.current - e?.deltaY);
    }, [dir, getRef, tabBarPosition, dragable, allItemW, tabsItems, updateMenus, setOffset]);

    // 单独处理 wheel 事件监听器，优化版本
    useEffect(() => {
        const navElement = getRef?.('nav');
        if (!navElement) return;

        // 优化的滚轮事件处理
        const optimizedMouseScroll = (e: WheelEvent) => {
            // 只在需要阻止默认行为时才阻止
            const shouldPreventDefault =
                (e.deltaY < 0 && prevRef.current) ||
                (e.deltaY > 0 && nextRef.current) ||
                dir === 'rtl';

            if (shouldPreventDefault) {
                e.preventDefault();
                e.stopPropagation();
                mouseScroll(e);
            }
        };

        // 使用已经检测好的 passive 支持（在文件顶部）
        const wheelOptions = supportsPassive ? { passive: false, capture: false } : false;
        navElement.addEventListener('wheel', optimizedMouseScroll, wheelOptions);

        return () => {
            // 确保使用相同的选项移除监听器
            if (navElement) {
                navElement.removeEventListener('wheel', optimizedMouseScroll, wheelOptions as any);
            }
        };
    }, [mouseScroll, getRef, dir]);

    const renderNavButton = (
        direction: 'prev' | 'next',
        canNavigate: boolean | undefined,
        onClick: any,
        icon: React.ReactNode,
        menu: React.ReactElement<any> | undefined
    ) => {
        const showNextPrev = prev || next;
        const dropdownArrowClassname = classnames({
            [`${clsPrefix}-more-select-truncation-dropdown`]: true,
            [`${clsPrefix}-more-select-dropdown`]: true,
            [`${clsPrefix}-more-select-dropdown-editable`]: type == 'editable-card'
        });
        const defaultIcon = direction === 'prev'
            ? <Icon fieldid={fieldid ? fieldid + '_prev_btn' : undefined} type="uf-anglepointingtoleft"/>
            : <Icon fieldid={fieldid ? fieldid + '_next_btn' : undefined} type="uf-anglearrowpointingtoright"/>;
        const buttonContent = (
            <span
                onClick={canNavigate ? onClick : undefined}
                // @ts-ignore
                unselectable="unselectable"
                className={classnames({
                    [`${clsPrefix}-tab-${direction}`]: 1,
                    [`${clsPrefix}-tab-btn-disabled`]: !canNavigate,
                    [`${clsPrefix}-tab-arrow-show`]: showNextPrev,
                })}
            >
                {icon || defaultIcon}
            </span>
        );

        if (isTruncationShow && menu) {
            return (
                <Dropdown
                    trigger={['hover']}
                    overlay={menu}
                    animation="slide-up"
                    overlayClassName={dropdownArrowClassname}
                >
                    {buttonContent}
                </Dropdown>
            );
        }

        return buttonContent;
    };

    // Render
    const showNextPrev = prev || next;

    const createMenu = (items: any) => (
        <Menu onClick={onSelect} fieldid={fieldid ? fieldid + '_tabs' : undefined} className={`${clsPrefix}-more-menu`}>
            {items}
        </Menu>
    );
    const menuLeft = createMenu(menuArrowLeft);
    const menuRight = createMenu(menuArrowRight);
    const prevButton = renderNavButton('prev', prev, (e: React.MouseEvent) => handleNavClick('prev', e), prevIcon, menuLeft);
    const nextButton = renderNavButton('next', next, (e: React.MouseEvent) => handleNavClick('next', e), nextIcon, menuRight);
    const menu = (
        <Menu onClick={onSelect} fieldid={fieldid ? fieldid + '_tabs' : undefined} className={`${clsPrefix}-more-menu`} selectedKeys={[activeKey as string]}>
            {menuArr}
        </Menu>
    )
    let dropdownClassname = classnames({
        [`${clsPrefix}-more-select-dropdown`]: true,
        [`${dropdownClassName}`]: dropdownClassName,
        [`${clsPrefix}-more-select-dropdown-${tabsUidRef.current}`]: true,
        [`${clsPrefix}-more-select-dropdown-editable`]: type == 'editable-card'
    });
    const moreTabsNode = (
        <Dropdown
            trigger={['hover']}
            overlay={menu}
            animation="slide-up"
            onVisibleChange={onVisibleChange}
            overlayClassName={dropdownClassname}

        >
            <span className={`${clsPrefix}-tab-more-select`}>
                { moreIcon ? moreIcon : <Icon fieldid={fieldid ? fieldid + '_select_btn' : undefined} type="uf-mulu"/> }
            </span>
        </Dropdown>
    )

    const navClassName = `${clsPrefix}-nav`;
    const navClasses = classnames({
        [navClassName]: true,
        [scrollAnimated ? `${navClassName}-animated` : `${navClassName}-no-animated`]: true,
    });

    return (
        <div
            className={classnames({
                [`${clsPrefix}-nav-container`]: 1,
                [`${clsPrefix}-nav-container-scrolling`]: showNextPrev && moreType === 'moreTabsArrow',
                [`${clsPrefix}-nav-more`]: showNextPrev && moreType === 'moreTabsSelect',
                [`${clsPrefix}-nav-container-allshow`]: showNextPrev && moreTypeAllShow,
            })}
            key="container"
            // @ts-ignore
            ref={saveRef!('container')}
            style={{minWidth: '32px'}}
        >
            {showNextPrev && (moreTypeAllShow || moreType === 'moreTabsArrow') && prevButton}
            {showNextPrev && (moreTypeAllShow || moreType === 'moreTabsArrow') && nextButton}
            {showNextPrev && (moreTypeAllShow || moreType === 'moreTabsSelect') && moreTabsNode}
            <div
                className={`${clsPrefix}-nav-wrap`}
                // @ts-ignore
                ref={saveRef!('navWrap')}
            >
                <div className={`${clsPrefix}-nav-scroll`}>
                    <div
                        className={navClasses}
                        // @ts-ignore
                        ref={saveRef!('nav')}
                    >
                        {navWrapper!(children)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScrollableTabBarNode;
